import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, Box } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import type { UserBasicDTO, UserCreateDTO, UserUpdateDTO } from '../models/userDTOs';
import { apiRequest } from '@/src/services';
import { Constants, getZeroOption } from '@/src/utils';
import {
    Loading,
    ControlledSingleSelect,
    ControlledChipMultiSelect,
    ControlledTextField,
    ControlledCheckbox,
    SimpleButton,
    ErrorMessage,
    StatusMessage,
} from '@/src/ui';
import {
    type InitialOptions,
    type ClassOption,
    type SchoolClassOptions,
} from '@/src/pages/rostering/users/models/filterDTOs';

const utm = Constants.USER_TYPE_MASTER_IDS;

const LoadingOption = getZeroOption('Loading...');
const DefaultUserTypeOption = getZeroOption('Select User Type...');
const DefaultGradeLevelOption = getZeroOption('Select Grade...');
const DefaultDistrictOption = getZeroOption('Select District...');
const DefaultSchoolOption = getZeroOption('Select School...');
const DefaultClassOption = getZeroOption('Select Class...');
const SelectDistrictOption = getZeroOption('Select District First');
const SelectSchoolOption = getZeroOption('Select School First');
const NoClassOption = getZeroOption('No Classes Available');

const schema = yup.object().shape({
    userTypeMasterId: yup
        .number()
        .required()
        .min(1, 'Select a user type')
        .notOneOf([1], 'Invalid user type'),
    gradeLevelMasterId: yup.number().required().min(1, 'Select a grade'),
    districtId: yup.number().required().min(1, 'District is required'),
    schoolId: yup.number().required().min(1, 'School is required'),
    classIds: yup
        .array()
        .of(yup.number())
        .optional()
        .test('no-zero', 'Invalid class selected', (value) => {
            return value ? !value.includes(0) : true;
        }),
    alternateSchoolIds: yup.array().of(yup.number()).optional(),
    fName: yup
        .string()
        .required('First name is required')
        .max(
            Constants.MAX_CHARS.NAME,
            `First name cannot exceed ${Constants.MAX_CHARS.NAME} characters`
        ),
    lName: yup
        .string()
        .required('Last name is required')
        .max(
            Constants.MAX_CHARS.NAME,
            `Last name cannot exceed ${Constants.MAX_CHARS.NAME} characters`
        ),
    username: yup
        .string()
        .required('Username is required')
        .max(
            Constants.MAX_CHARS.USERNAME,
            `Username cannot exceed ${Constants.MAX_CHARS.USERNAME} characters`
        ),
    password: yup
        .string()
        .ensure()
        .when({
            is: (val: string) => val && val.length > 0,
            then: (schema) =>
                schema
                    .min(8, 'Password must be at least 8 characters')
                    .matches(
                        /^(?=.*[A-Z])(?=.*[^A-Za-z])/,
                        'Password must contain at least one uppercase letter and one number or special character'
                    ),
            otherwise: (schema) => schema.notRequired(),
        }),
    email: yup
        .string()
        .email('Invalid email')
        .required('Email is required')
        .max(
            Constants.MAX_CHARS.EMAIL,
            `Email cannot exceed ${Constants.MAX_CHARS.EMAIL} characters`
        ),
    accessEndDUTC: yup.date().nullable(),
    isMiddleSchool: yup.boolean().required(),
    isAllGradeViewEnabled: yup.boolean().optional(),
    ssid: yup
        .string()
        .optional()
        .max(Constants.MAX_CHARS.SSID, `SSID cannot exceed ${Constants.MAX_CHARS.SSID} characters`),
    ignoreGradeLevel: yup.boolean().optional(),
});

const sanitizeDate = (value: any) => {
    if (!value) return value;
    const date = value instanceof Date ? new Date(value) : new Date(value.toString());
    date.setSeconds(0, 0);
    return date;
};

interface CreateEditModalProps {
    open: boolean;
    modalMode: 'create' | 'edit' | null;
    handleClose: (_shouldRefresh: boolean, _isInTimeout: boolean) => void;
    resourceId: number | undefined;
}

// TODO: Verify username/email/SSID not taken
const CreateEditModal: React.FC<CreateEditModalProps> = ({
    open,
    modalMode,
    handleClose,
    resourceId,
}) => {
    const hasLoaded = useRef(false);
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);

    const [allOptions, setAllOptions] = useState({
        userTypeMasterOptions: [LoadingOption],
        gradeLevelMasterOptions: [LoadingOption],
        districtOptions: [LoadingOption],
        schoolOptions: [SelectDistrictOption],
        classOptions: [SelectDistrictOption],
    });

    const [classData, setClassData] = useState<Array<ClassOption>>([]);

    // React Hook Form setup
    const {
        control,
        setValue,
        getValues,
        reset,
        watch,
        formState,
        handleSubmit,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            userTypeMasterId: 0,
            fName: '',
            lName: '',
            email: '',
            username: '',
            password: '',
            gradeLevelMasterId: 1,
            districtId: 0,
            schoolId: 0,
            classIds: [],
            alternateSchoolIds: [],
            ssid: '',
            isMiddleSchool: false,
            isAllGradeViewEnabled: false,
            accessEndDUTC: null,
            ignoreGradeLevel: modalMode === 'edit',
        },
    });

    const districtId = watch('districtId');
    const schoolId = watch('schoolId');
    const gradeLevelMasterId = watch('gradeLevelMasterId');
    const userTypeMasterId = watch('userTypeMasterId');
    const ignoreGradeLevel = watch('ignoreGradeLevel');

    // Get schools and classes on district change
    useEffect(() => {
        if (!Object.keys(formState.touchedFields).length) return;
        loadData(2, districtId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [districtId]);
    // Update class dropdown options when filters change
    useEffect(() => {
        if (!Object.keys(formState.touchedFields).length) return;
        loadData(3, districtId, classData, gradeLevelMasterId, schoolId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classData, schoolId, gradeLevelMasterId, ignoreGradeLevel]);
    // Reset values if user type changed
    useEffect(() => {
        if (userTypeMasterId === utm.DISTRICT_ADMIN || userTypeMasterId === utm.SCHOOL_ADMIN) {
            setValue('gradeLevelMasterId', 1);
        }
        if (!Object.keys(formState.touchedFields).length || modalMode === 'edit') return;
        setValue('email', '');
        setValue('classIds', []);
        setValue('alternateSchoolIds', []);
        setValue('gradeLevelMasterId', 0);
        setValue('isMiddleSchool', false);
        setValue('ssid', '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userTypeMasterId]);

    // One function to load all data. Separate functions were causing issues with state updates
    // in the useEffect hooks above
    const loadData = useCallback(
        async (
            loadMode: number,
            districtId: number | string = getValues('districtId'),
            newClassData: Array<ClassOption> = classData,
            gradeLevelMasterId: number | string = getValues('gradeLevelMasterId'),
            schoolId: number | string = getValues('schoolId')
        ) => {
            let userData = null;
            const isEditFirstLoad = modalMode === 'edit' && loadMode === 1;

            // Initial load
            if (loadMode === 1) {
                try {
                    const res = await apiRequest<InitialOptions>(
                        'GET',
                        `utility/filterData?getUserTypeMaster=true&getGradeLevelMaster=true&getDistrict=true`
                    );
                    const data = res.data;

                    const newUserTypeOptions = data.userTypeMasterOptions.filter((o) => {
                        return o.id !== utm.SUPER_ADMIN;
                    });
                    setAllOptions((prev) => ({
                        ...prev,
                        userTypeMasterOptions: [DefaultUserTypeOption, ...newUserTypeOptions],
                    }));
                    if (!isEditFirstLoad)
                        setValue('userTypeMasterId', Number(DefaultUserTypeOption.id));

                    setAllOptions((prev) => ({
                        ...prev,
                        gradeLevelMasterOptions: [
                            DefaultGradeLevelOption,
                            ...data.gradeLevelMasterOptions,
                        ],
                    }));
                    if (!isEditFirstLoad)
                        setValue('gradeLevelMasterId', Number(DefaultGradeLevelOption.id));

                    const newDistrictOptions = data.districtOptions.sort((a, b) =>
                        a.name.localeCompare(b.name)
                    );
                    setAllOptions((prev) => ({
                        ...prev,
                        districtOptions: [DefaultDistrictOption, ...newDistrictOptions],
                    }));
                    if (!isEditFirstLoad) setValue('districtId', Number(DefaultDistrictOption.id));
                } catch {
                    setError('Failed to fetch grade and district options.');
                    hasLoaded.current = true;
                    return;
                }

                if (modalMode === 'edit') {
                    try {
                        const res = await apiRequest<UserBasicDTO>(
                            'GET',
                            `dbo/users/${resourceId}`
                        );
                        userData = res.data;
                        if (userData === null) throw new Error('Unable to load user');

                        // Set edit mode values if user is found
                        districtId = userData.districtId || 0;
                        schoolId = userData.schoolId || 0;
                        gradeLevelMasterId = userData.gradeLevelMasterId || 0;
                        schoolId = userData.schoolId || 0;
                    } catch {
                        setError('Failed to load user data');
                    }
                } else if (modalMode === 'create') {
                    hasLoaded.current = true;
                }
            }

            // Get schools and classes based on district
            if (loadMode === 2 || isEditFirstLoad) {
                try {
                    const res = await apiRequest<SchoolClassOptions>(
                        'GET',
                        `utility/filterData?getSchool=true&getClass=true&districtId=${districtId}`
                    );
                    const data = res.data;

                    const sortedSchools = data.schoolOptions
                        .slice()
                        .sort((a, b) => a.name.localeCompare(b.name));
                    setAllOptions((prev) => ({
                        ...prev,
                        schoolOptions: [DefaultSchoolOption, ...sortedSchools],
                    }));
                    if (!isEditFirstLoad) setValue('schoolId', Number(DefaultSchoolOption.id));

                    setAllOptions((prev) => ({
                        ...prev,
                        classOptions: [SelectSchoolOption],
                    }));
                    if (!isEditFirstLoad) setValue('classIds', []);

                    const sortedClasses = data.classOptions
                        .slice()
                        .sort((a, b) => a.name.localeCompare(b.name));
                    setClassData(sortedClasses);
                    newClassData = sortedClasses;
                } catch {
                    setError('Failed to fetch school and class options');
                }
            }

            // Update class dropdowns
            if (loadMode === 3 || isEditFirstLoad) {
                try {
                    if (!schoolId) {
                        setAllOptions((prev) => ({
                            ...prev,
                            classOptions: [SelectSchoolOption],
                        }));
                        setValue('classIds', []);
                        if (!isEditFirstLoad) return;
                    }

                    if (!newClassData) throw new Error('Class data cannot be null');
                    const newClassOptions = newClassData
                        .filter((option) => {
                            if (schoolId && option.schoolId !== Number(schoolId)) {
                                return false;
                            }
                            if (!ignoreGradeLevel) {
                                const glids = option.gradeLevelMasterIds;
                                const doGradeOptionsExist = glids && glids.length > 0;
                                if (
                                    gradeLevelMasterId &&
                                    doGradeOptionsExist &&
                                    !glids.includes(Number(gradeLevelMasterId))
                                ) {
                                    return false;
                                }
                            }
                            return true;
                        })
                        .map((option) => ({
                            id: option.id,
                            name: option.name,
                        }));

                    if (!newClassOptions.length) {
                        setAllOptions((prev) => ({
                            ...prev,
                            classOptions: [NoClassOption],
                        }));
                        setValue('classIds', []);
                        if (!isEditFirstLoad) return;
                    }

                    setAllOptions((prev) => ({
                        ...prev,
                        classOptions: [DefaultClassOption, ...newClassOptions],
                    }));
                    setValue('classIds', []);
                } catch {
                    setError('Class data could not be loaded');
                }
            }

            if (isEditFirstLoad) {
                if (userData === null) {
                    setError('Unable to load user data');
                    hasLoaded.current = true;
                    return;
                }

                // Reset form values so they populate correctly
                const u = userData;
                reset({
                    userTypeMasterId: u.userTypeMasterId || 0,
                    fName: u.fName || '',
                    lName: u.lName || '',
                    email: u.email || '',
                    username: u.username || '',
                    password: '',
                    gradeLevelMasterId: u.gradeLevelMasterId || 0,
                    districtId: u.districtId || 0,
                    schoolId: u.schoolId || 0,
                    classIds: (u.classTeachers || u.classStudents || []).map((c) => c.classId),
                    alternateSchoolIds: (u.userSchools || []).map((s) => s.schoolId),
                    isMiddleSchool: u.isMiddleSchool,
                    isAllGradeViewEnabled: (u as any).isAllGradeViewEnabled || false,
                    accessEndDUTC: u.accessEndDUTC ? new Date(u.accessEndDUTC) : null,
                    ssid: u.ssid || '',
                    ignoreGradeLevel: getValues().ignoreGradeLevel || false,
                });
            }

            hasLoaded.current = true;
        },
        [classData, getValues, ignoreGradeLevel, modalMode, reset, resourceId, setValue]
    );

    // Initialize
    useEffect(() => {
        loadData(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // yup handles validation through handleSubmit in the form component
    const onSubmit = async () => {
        if (submitting || success) return;

        const values = getValues();

        if (modalMode === 'create') {
            const newUser: UserCreateDTO = {
                fName: values.fName,
                lName: values.lName,
                userTypeMasterId: values.userTypeMasterId,
                username: values.username,
                password: values.password || undefined,
                email: values.email,
                gradeLevelMasterId: [utm.DISTRICT_ADMIN, utm.SCHOOL_ADMIN].includes(
                    values.userTypeMasterId
                )
                    ? 1
                    : values.gradeLevelMasterId,
                districtId: values.districtId,
                schoolId: values.schoolId,
                alternateSchoolIds:
                    values.userTypeMasterId === utm.SCHOOL_ADMIN
                        ? (values.alternateSchoolIds as number[])
                        : [],
                classIds: [utm.TEACHER, utm.STUDENT].includes(values.userTypeMasterId)
                    ? (values.classIds as number[])
                    : [],
                isMiddleSchool: values.isMiddleSchool,
                isAllGradeViewEnabled: values.isAllGradeViewEnabled,
                accessEndDUTC: values.accessEndDUTC || null,
                ssid: values.userTypeMasterId === utm.STUDENT ? values.ssid || '' : '',
            };

            setSubmitting(true);
            setError('');
            try {
                await apiRequest<number>('POST', 'dbo/users', newUser);
                setSuccess(true);
                setTimeout(() => {
                    handleClose(true, false);
                    setSuccess(false);
                }, 750);
            } catch {
                setError('Failed to create user');
                return;
            } finally {
                setSubmitting(false);
            }
        } else if (modalMode === 'edit') {
            if (!resourceId) setError('Unable to load user Id');

            const updateUser: UserUpdateDTO = {
                id: resourceId!,
                fName: values.fName,
                lName: values.lName,
                username: values.username,
                password: values.password || undefined,
                email: values.email,
                gradeLevelMasterId: values.gradeLevelMasterId,
                districtId: values.districtId,
                schoolId: values.schoolId,
                alternateSchoolIds:
                    values.userTypeMasterId === utm.SCHOOL_ADMIN
                        ? (values.alternateSchoolIds as number[])
                        : [],
                classIds: [utm.TEACHER, utm.STUDENT].includes(values.userTypeMasterId)
                    ? (values.classIds as number[])
                    : [],
                isMiddleSchool: values.isMiddleSchool,
                isAllGradeViewEnabled: values.isAllGradeViewEnabled,
                accessEndDUTC: values.accessEndDUTC || null,
                ssid: values.userTypeMasterId === utm.STUDENT ? values.ssid || '' : '',
            };

            setSubmitting(true);
            setError('');
            try {
                await apiRequest<number>('PUT', `dbo/users/${resourceId}`, updateUser);
                setSuccess(true);
                setTimeout(() => {
                    handleClose(true, false);
                    setSuccess(false);
                }, 750);
            } catch {
                setError('Failed to update user');
            } finally {
                setSubmitting(false);
            }
        }
    };

    return (
        <Modal open={open} onClose={() => handleClose(false, success)}>
            <Box
                sx={{
                    p: 4,
                    bgcolor: 'background.paper',
                    margin: 'auto',
                    mt: 8,
                    width: 500,
                    overflowY: 'auto',
                    maxHeight: '90vh',
                }}
            >
                <h2>{modalMode === 'create' ? 'Create User' : 'Edit User'}</h2>
                <br />
                {!hasLoaded.current ? (
                    <Loading message="Loading..." size="lg" />
                ) : (
                    <form onSubmit={handleSubmit(onSubmit)}>
                        {/* User type can only be set on creation */}
                        {modalMode === 'create' && (
                            <ControlledSingleSelect
                                name="userTypeMasterId"
                                control={control}
                                options={allOptions.userTypeMasterOptions}
                                error={errors.userTypeMasterId}
                                label="User Type"
                                onChange={(e, field) => {
                                    const value = Number(e.target.value);
                                    if (value !== 0) {
                                        setValue('userTypeMasterId', value);
                                        field.onChange(value);
                                    }
                                }}
                            />
                        )}

                        <ControlledSingleSelect
                            name="districtId"
                            control={control}
                            options={allOptions.districtOptions}
                            error={errors.districtId}
                            label="District"
                            onChange={(e, field) => {
                                const value = Number(e.target.value);
                                if (value !== 0) {
                                    setValue('districtId', value);
                                    field.onChange(value);
                                }
                            }}
                        />

                        <ControlledSingleSelect
                            name="schoolId"
                            control={control}
                            options={allOptions.schoolOptions}
                            error={errors.schoolId}
                            label="School"
                            onChange={(e, field) => {
                                const value = Number(e.target.value);
                                if (value !== 0) {
                                    setValue('schoolId', value);
                                    field.onChange(value);
                                }
                            }}
                        />

                        {/* Grade level for School Admin and District Admin will be set to 1 */}
                        {![utm.SCHOOL_ADMIN, utm.DISTRICT_ADMIN].includes(userTypeMasterId) && (
                            <>
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <ControlledSingleSelect
                                        name="gradeLevelMasterId"
                                        control={control}
                                        options={allOptions.gradeLevelMasterOptions}
                                        error={errors.gradeLevelMasterId}
                                        label="Grade"
                                        onChange={(e) => {
                                            const value = Number(e.target.value);
                                            if (value !== 0) setValue('gradeLevelMasterId', value);
                                        }}
                                    />
                                    <ControlledCheckbox
                                        name="isMiddleSchool"
                                        control={control}
                                        label="Middle School"
                                        color="primary"
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <ControlledCheckbox
                                        name="isAllGradeViewEnabled"
                                        control={control}
                                        label="Is All Grade View Enabled"
                                        color="primary"
                                    />
                                </Box>
                            </>
                        )}

                        {/* Class Selector: only show if teacher or student is selected */}
                        {/* Alternate Schools Selector: only show if school admin is selected */}
                        {userTypeMasterId === utm.SCHOOL_ADMIN && (
                            <ControlledChipMultiSelect
                                name="alternateSchoolIds"
                                label="Alternate Schools"
                                control={control}
                                error={errors.alternateSchoolIds}
                                options={allOptions.schoolOptions.filter(
                                    (option) => option.id !== Number(schoolId)
                                )}
                                onChange={(event, field) => {
                                    const value = Array.isArray(event.target.value)
                                        ? event.target.value
                                        : [event.target.value];
                                    if (!value.some((v) => Number(v) === 0)) field.onChange(value);
                                }}
                                maxHeight={400}
                            />
                        )}
                        {[utm.TEACHER, utm.STUDENT].includes(userTypeMasterId) && (
                            <>
                                <ControlledCheckbox
                                    name="ignoreGradeLevel"
                                    control={control}
                                    label="Show classes in all grades"
                                    color="primary"
                                />
                                <ControlledChipMultiSelect
                                    name="classIds"
                                    label="Classes"
                                    control={control}
                                    error={errors.classIds}
                                    options={allOptions.classOptions}
                                    onChange={(event, field) => {
                                        const value = Array.isArray(event.target.value)
                                            ? event.target.value
                                            : [event.target.value];
                                        if (!value.some((v) => Number(v) === 0))
                                            field.onChange(value);
                                    }}
                                    maxHeight={400}
                                />
                            </>
                        )}

                        {/* Common fields for all users */}
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <ControlledTextField
                                name="fName"
                                label="First Name"
                                control={control}
                                error={errors.fName}
                                variant="outlined"
                                maxLength={Constants.MAX_CHARS.NAME}
                            />
                            <ControlledTextField
                                name="lName"
                                label="Last Name"
                                control={control}
                                error={errors.lName}
                                variant="outlined"
                                maxLength={Constants.MAX_CHARS.NAME}
                            />
                        </Box>

                        <ControlledTextField
                            name="username"
                            label="Username"
                            control={control}
                            error={errors.username}
                            variant="outlined"
                            maxLength={Constants.MAX_CHARS.USERNAME}
                        />

                        <ControlledTextField
                            name="password"
                            label="Password"
                            control={control}
                            error={errors.password}
                            variant="outlined"
                            type="password"
                            autocomplete="new-password"
                            maxLength={Constants.MAX_CHARS.PASSWORD}
                        />
                        <div
                            style={{
                                fontSize: '0.8rem',
                                color: '#666',
                                marginTop: '10px',
                                marginBottom: '10px',
                            }}
                        >
                            Password must contain at least one uppercase letter and one number or
                            special character
                            {modalMode === 'create' ? ' (Leave blank for no password login)' : ''}.
                        </div>

                        <ControlledTextField
                            name="email"
                            label="Email"
                            control={control}
                            error={errors.email}
                            variant="outlined"
                            maxLength={Constants.MAX_CHARS.EMAIL}
                        />

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                            <Controller
                                name="accessEndDUTC"
                                control={control}
                                render={({ field, fieldState }) => (
                                    <DatePicker
                                        {...field}
                                        label="Access End Date"
                                        onChange={(newValue) =>
                                            field.onChange(sanitizeDate(newValue))
                                        }
                                        slotProps={{
                                            textField: {
                                                error: !!fieldState.error,
                                                helperText: fieldState.error?.message,
                                                fullWidth: true,
                                                margin: 'normal',
                                            },
                                        }}
                                    />
                                )}
                            />
                            <SimpleButton
                                color="error"
                                onClick={() => setValue('accessEndDUTC', null)}
                                sx={{ minHeight: 40, height: 40, alignSelf: 'center' }}
                            >
                                Remove
                            </SimpleButton>
                        </Box>

                        {userTypeMasterId === utm.STUDENT && (
                            <ControlledTextField
                                name="ssid"
                                label="SSID"
                                control={control}
                                error={errors.ssid}
                                variant="outlined"
                                maxLength={Constants.MAX_CHARS.SSID}
                            />
                        )}

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                            <SimpleButton
                                type="button"
                                onClick={() => handleClose(false, false)}
                                disabled={submitting || success}
                                color="warning"
                                variant="outlined"
                                sx={{ minHeight: 40, height: 40, alignSelf: 'center' }}
                            >
                                Cancel
                            </SimpleButton>
                            <SimpleButton
                                type="submit"
                                disabled={submitting || success}
                                variant="contained"
                                sx={{ minHeight: 40, height: 40, alignSelf: 'center' }}
                            >
                                Submit
                            </SimpleButton>
                        </Box>
                        <ErrorMessage error={error} />
                        <StatusMessage
                            visible={success}
                            status={`User ${modalMode === 'create' ? 'created' : 'updated'} successfully!`}
                            style={{ marginTop: 8 }}
                        />
                        <Loading visible={submitting} message="Processing..." size="sm" />
                    </form>
                )}
            </Box>
        </Modal>
    );
};

export default CreateEditModal;
